import * as React from 'react';

import { WorkOrderStatus } from '@rematter/frontend-lib-api-client';
import {
  Badge,
  DataGridColumn,
  LocalizedDate,
  maybeEmptyInfo,
} from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { ThemeColor } from '@rematter/ui-web/types';

import { SortingQueueFlattenedItem } from '../../../_graphql/querySortingQueue';
import { useTranslations } from '../../../i18n/useTranslations';

const getSortingStatusColor = (status: WorkOrderStatus | null): ThemeColor => {
  if (!status || status === 'Draft') {
    return 'warning';
  }
  if (status === 'Open' || status === 'InProgress') {
    return 'teal';
  }
  if (status === 'Completed') {
    return 'success';
  }
  if (status === 'Cancelled') {
    return 'neutral';
  }
  return 'neutral';
};

type StatusTranslations = {
  pending: string;
  inProgress: string;
  completed: string;
  cancelled: string;
  unknown: string;
};

const getSortingStatusText = (
  status: WorkOrderStatus | null,
  t: StatusTranslations,
): string => {
  if (!status || status === 'Draft') {
    return t.pending;
  }
  if (status === 'Open' || status === 'InProgress') {
    return t.inProgress;
  }
  if (status === 'Completed') {
    return t.completed;
  }
  if (status === 'Cancelled') {
    return t.cancelled;
  }
  return t.unknown;
};

type SortingStatusBadgeProps = {
  status: WorkOrderStatus | null;
  statusTranslations: StatusTranslations;
  size?: 'small' | 'xs' | 'default' | 'large';
  className?: string;
};

const SortingStatusBadge = ({
  status,
  statusTranslations,
  size = 'small',
  className,
}: SortingStatusBadgeProps) => {
  const color = getSortingStatusColor(status);
  const text = getSortingStatusText(status, statusTranslations);

  return (
    <Badge
      color={color}
      size={size}
      className={cn('@SortingStatusBadge', className)}
    >
      {text}
    </Badge>
  );
};

type SettlementBasisBadgeProps = {
  basis: string;
  settlementBasisTranslations: { postSort: string; preSort: string };
  size?: 'small' | 'xs' | 'default' | 'large';
  className?: string;
};

const SettlementBasisBadge = ({
  basis,
  settlementBasisTranslations,
  size = 'small',
  className,
}: SettlementBasisBadgeProps) => {
  const isPostSort = basis === 'PostSort';

  return (
    <Badge
      color={isPostSort ? 'primary' : 'neutral'}
      size={size}
      className={cn('@SettlementBasisBadge', className)}
    >
      {isPostSort
        ? settlementBasisTranslations.postSort
        : settlementBasisTranslations.preSort}
    </Badge>
  );
};

type UseSortingQueueColumnsArgs = {
  onPurchaseLoadClick: (item: SortingQueueFlattenedItem) => void;
};

export const useSortingQueueColumns = ({
  onPurchaseLoadClick,
}: UseSortingQueueColumnsArgs): DataGridColumn<SortingQueueFlattenedItem>[] => {
  const t = useTranslations();

  return React.useMemo(() => {
    const columnDefs: DataGridColumn<SortingQueueFlattenedItem>[] = [
      {
        key: 'purchaseLoadDocumentNumber',
        header: t.sortingQueue.columns.purchaseNumber,
        cell: ({ row }) => (
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => onPurchaseLoadClick(row)}
          >
            {row.purchaseLoadDocumentNumber}
          </button>
        ),
      },
      {
        key: 'receivedDate',
        header: t.sortingQueue.columns.receivedDate,
        cell: ({ row }) =>
          maybeEmptyInfo(row.receivedDate, (v) => <LocalizedDate date={v} />),
      },
      {
        key: 'supplierName',
        header: t.sortingQueue.columns.supplier,
        cell: ({ row }) => maybeEmptyInfo(row.supplierName),
      },
      {
        key: 'facilityName',
        header: t.sortingQueue.columns.facility,
        cell: ({ row }) => maybeEmptyInfo(row.facilityName),
      },
      {
        key: 'itemName',
        header: t.sortingQueue.columns.item,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span>{row.itemName ?? '-'}</span>
            {row.itemSku && (
              <span className="text-xs text-neutral/60">{row.itemSku}</span>
            )}
          </div>
        ),
      },
      {
        key: 'netWeight',
        header: t.sortingQueue.columns.netWeight,
        cell: ({ row }) =>
          row.netWeight
            ? `${row.netWeight.value.toLocaleString()} ${row.netWeight.unit}`
            : '-',
      },
      {
        key: 'settlementBasis',
        header: t.sortingQueue.columns.settlementBasis,
        cell: ({ row }) => (
          <SettlementBasisBadge
            basis={row.settlementBasis}
            settlementBasisTranslations={t.sortingQueue.settlementBasis}
          />
        ),
      },
      {
        key: 'sortingStatus',
        header: t.sortingQueue.columns.sortingStatus,
        cell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <SortingStatusBadge
              status={row.sortingWorkOrderStatus}
              statusTranslations={t.sortingQueue.status}
            />
            {row.sortingWorkOrderNumber && (
              <span className="text-xs text-neutral/60">
                WO# {row.sortingWorkOrderNumber}
              </span>
            )}
          </div>
        ),
      },
    ];

    return columnDefs;
  }, [onPurchaseLoadClick, t]);
};
